import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Heart, Plus, X, Star as StarIcon } from "lucide-react";
import { PageHeader, Panel, Stars, fmtD, fmtDT } from "@/components/admin/ui";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-neutral-50 p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="font-display font-bold text-xl mt-0.5">{value}</div>
    </div>
  );
}

export default function CustomerProfile() {
  const { uid } = useParams();
  const [d, setD] = useState(null);
  const [newFav, setNewFav] = useState("");

  const load = useCallback(() => { api.get(`/customers/${uid}/profile`).then((r) => setD(r.data)).catch(() => setD(false)); }, [uid]);
  useEffect(load, [load]);

  const saveFavs = async (items) => {
    try { await api.put(`/customers/${uid}/favorites`, { items }); setNewFav(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (d === null) return <div className="p-10 text-neutral-500">Loading…</div>;
  if (d === false) return <div className="p-10 text-neutral-500">Customer not found.</div>;
  const c = d.customer; const L = d.loyalty;

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <Link to="/admin/customers" className="text-xs text-neutral-500 inline-flex items-center gap-1 mb-4 hover:text-neutral-900" data-testid="back-customers"><ArrowLeft className="w-3 h-3"/>All customers</Link>
      <PageHeader eyebrow="Customer" title={c.name || c.email} testId="customer-profile-name" subtitle={`${c.email}${c.phone ? ` · ${c.phone}` : ""}`}/>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Date joined" value={fmtD(c.created_at)} testId="cp-joined"/>
        <Stat label="Last order" value={fmtD(d.last_order_at)} testId="cp-last"/>
        <Stat label="Orders" value={L.total_orders} testId="cp-orders"/>
        <Stat label="Loyalty points" value={L.points} testId="cp-points"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel className="p-5 lg:col-span-2">
          <h2 className="font-display font-semibold mb-3">Order History</h2>
          {d.orders.length === 0 && <p className="text-sm text-neutral-500" data-testid="cp-orders-empty">No orders yet.</p>}
          <div className="divide-y divide-neutral-100">
            {d.orders.map((o) => (
              <div key={o.id} className="py-3 text-sm" data-testid={`cp-order-${o.id}`}>
                <div className="flex justify-between"><span className="font-medium">{fmtD(o.created_at)} <span className="text-neutral-400 font-mono text-xs">{o.order_number}</span></span><span className="font-semibold">${(o.total || 0).toFixed(2)}</span></div>
                <ul className="text-neutral-600 mt-1">{o.items.map((i, idx) => <li key={idx}>{i.quantity}× {i.name}</li>)}</ul>
              </div>
            ))}
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel className="p-5">
            <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-[hsl(var(--primary))]"/>Customer Favorites</h2>
            {d.favorites.auto.length === 0 && d.favorites.manual.length === 0 && <p className="text-sm text-neutral-500" data-testid="cp-favs-empty">No favorites yet.</p>}
            <ul className="space-y-1 text-sm">
              {d.favorites.auto.map((n) => <li key={`a-${n}`} className="flex items-center gap-2" data-testid="cp-fav-auto"><StarIcon className="w-3 h-3 text-amber-400 fill-amber-400"/>{n}<span className="text-[10px] text-neutral-400">frequent</span></li>)}
              {d.favorites.manual.map((n) => (
                <li key={`m-${n}`} className="flex items-center gap-2" data-testid="cp-fav-manual"><Heart className="w-3 h-3 text-rose-500 fill-rose-500"/>{n}
                  <button onClick={() => saveFavs(d.favorites.manual.filter((x) => x !== n))} className="text-neutral-400 hover:text-red-600"><X className="w-3 h-3"/></button></li>
              ))}
            </ul>
            <div className="flex gap-2 mt-3">
              <Input value={newFav} onChange={(e) => setNewFav(e.target.value)} placeholder="Mark a favorite…" className="h-9" data-testid="cp-fav-input"/>
              <Button size="sm" onClick={() => newFav.trim() && saveFavs([...d.favorites.manual, newFav.trim()])} data-testid="cp-fav-add"><Plus className="w-4 h-4"/></Button>
            </div>
          </Panel>

          <Panel className="p-5">
            <h2 className="font-display font-semibold mb-3">Loyalty</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-neutral-500">Points</dt><dd className="font-semibold text-right">{L.points}</dd>
              <dt className="text-neutral-500">Rewards earned</dt><dd className="text-right">{L.rewards_earned}</dd>
              <dt className="text-neutral-500">Rewards redeemed</dt><dd className="text-right">{L.rewards_redeemed}</dd>
              <dt className="text-neutral-500">Available rewards</dt><dd className="text-right">{L.available_rewards}</dd>
              <dt className="text-neutral-500">Total visits</dt><dd className="text-right">{L.total_visits}</dd>
            </dl>
          </Panel>
        </div>
      </div>

      <Panel className="p-5 mt-4">
        <h2 className="font-display font-semibold mb-3">Customer Feedback</h2>
        {d.feedback.length === 0 && <p className="text-sm text-neutral-500" data-testid="cp-feedback-empty">No feedback yet.</p>}
        <div className="divide-y divide-neutral-100">
          {d.feedback.map((f) => (
            <div key={f.id} className="py-3 text-sm flex items-start justify-between gap-4" data-testid={`cp-feedback-${f.id}`}>
              <div><Stars value={f.rating}/>{f.comment && <p className="mt-1 text-neutral-700">{f.comment}</p>}</div>
              <div className="text-xs text-neutral-500 text-right whitespace-nowrap">{f.store_name}<br/>{fmtDT(f.created_at)}{f.order_id && <><br/>order {f.order_id.slice(0, 6)}</>}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
